
import {
  Component, Watch,
  Mixins,
} from 'vue-property-decorator';
import excerpts from 'excerpts';
import {
  PermissionCode,
} from '@/types';
import LeadCategories from '@/components/LeadCategories.vue';
import LeadTags from '@/components/LeadTags.vue';
import OperationComponentMixin from '@/mixins/OperationComponentMixin.vue';
import { leadRouteCode } from '@/helpers/leadCode';

const statusDetails = {
  draft: {
    long: 'Bozza',
    color: 'blue-grey',
    dark: true,
  },
  new: {
    long: 'Nuova',
    color: 'blue',
    dark: true,
  },
  open: {
    long: 'In Lavorazione',
    color: 'orange',
    dark: true,
  },
  pending: {
    long: 'In Attesa',
    color: 'amber',
    dark: true,
  },
  resolved: {
    long: 'Completata',
    color: 'green',
    dark: true,
  },
  closed: {
    long: 'Chiusa',
    color: 'grey',
    dark: true,
  },
  rejected: {
    long: 'Respinta',
    color: 'red',
    dark: true,
  },
  cancelled: {
    long: 'Annullata',
    color: 'grey',
    dark: true,
  },
} as {
  [key: string]: {
    long: string;
    color: string;
    dark: boolean;
  };
};

@Component({
  components: {
    LeadCategories,
    LeadTags,
  },
})
export default class OperationChildStatus extends Mixins(OperationComponentMixin) {
  sharedNotes: string | null = null
  getSharedNotesTimeout: number | undefined

  privateNotes: string | null = null
  getPrivateNotesTimeout: number | undefined

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get status() {
    return this.operationChild?.status;
  }

  get long() {
    return this.status && this.status in statusDetails ? statusDetails[this.status].long : null;
  }

  get color() {
    return this.status && this.status in statusDetails ? statusDetails[this.status].color : null;
  }

  get dark() {
    return this.status && this.status in statusDetails ? statusDetails[this.status].dark : null;
  }

  get title() {
    return this.operationChild?.title || 'Lead figlia';
  }

  get type() {
    switch (this.operationChild?.type) {
      case 'installatore-figlia':
        return 'Lead figlia generica';

      case 'elettrica-figlia':
        return 'Lead figlia elettrica';

      case 'termica-figlia':
        return 'Lead figlia termica';

      default:
        break;
    }

    return null;
  }

  get icon() {
    switch (this.operationChild?.type) {
      case 'installatore-figlia':
        return 'mdi-circle-medium';

      case 'elettrica-figlia':
        return 'mdi-power-plug';

      case 'termica-figlia':
        return 'mdi-radiator';

      default:
        break;
    }

    return null;
  }

  get hasNotes(): boolean {
    return this.hasSharedNotes || this.hasPrivateNotes;
  }

  get hasSharedNotes(): boolean {
    return this.operationChild?.hasSharedNotes === true;
  }

  get hasPrivateNotes(): boolean {
    return this.operationChild?.hasPrivateNotes === true;
  }

  // L'utente ha il permesso di vedere una o entrambe le note presenti
  get hasNotesPermission(): boolean {
    // Se l'utente è owner o requester, non può vedere le note
    if (!this.isCurrentUserOwnerOrRequester) return false;

    // Ci sono note condivise e l'utente ha il permesso di vederle
    if (this.hasSharedNotes && this.hasPermission('shared_notes.read')) return true;

    // Ci sono note interne e l'utente ha il permesso di vederle
    if (this.hasPrivateNotes && this.hasPermission('private_notes.read')) return true;

    return false;
  }

  get otherAttachments(): number {
    return this.operationChild?.otherAttachments || 0;
  }

  get poolUpdating(): { [key: number]: boolean } {
    return this.$store.getters.currentOperationsPoolUpdating;
  }

  get amIUpdating(): boolean {
    return this.operationChild !== null
      && this.operationChild.id in this.poolUpdating
      && this.poolUpdating[this.operationChild.id] === true;
  }

  @Watch('amIUpdating')
  onAmIUpdating(val: boolean, oldVal: boolean) {
    console.log(`OperationChildStatus: onAmIUpdating() val = ${val} oldVal ${oldVal}`); // eslint-disable-line no-console

    if (oldVal === true && val === false) {
      // La commessa è stata aggiornata
      // Se sono stati scaricati in precedenza dei dettagli...
      if (this.sharedNotes) {
        // ... recupero la nuova versione delle note condivise
        this.getSharedNotes();
      }

      if (this.privateNotes) {
        // ... recupero la nuova versione delle note condivise
        this.getPrivateNotes();
      }
    }
  }

  async getSharedNotes() {
    if (!this.operationChild) return Promise.resolve();

    try {
      const notes = await this.$store.dispatch('getSharedNotes', { id: this.operationChild.id });

      if (notes) {
        this.sharedNotes = excerpts(notes, {
          words: 50,
        });
      }
    } catch (error) {
      console.log('OperationChildStatus: error =', error); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  async getPrivateNotes() {
    if (!this.operationChild) return Promise.resolve();

    try {
      const notes = await this.$store.dispatch('getPrivateNotes', { id: this.operationChild.id });

      if (notes) {
        this.privateNotes = excerpts(notes, {
          words: 50,
        });
      }
    } catch (error) {
      console.log('OperationChildStatus: error =', error); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  onTooltipShow() {
    console.log('OperationChildStatus: onTooltipShow()'); // eslint-disable-line no-console

    if (this.hasSharedNotes
      && this.hasPermission('shared_notes.read')
      && !this.sharedNotes) {
      // La lead ha delle note condivise non scaricate e
      // l'utente ha il permesso di vederle.

      // Scarica le note condivise (tra un secondo)
      this.getSharedNotesTimeout = setTimeout(async () => {
        console.log('OperationChildStatus: onTooltipShow() timeout!'); // eslint-disable-line no-console

        this.getSharedNotes();
      }, 1000);
    }

    if (this.hasPrivateNotes
      && this.hasPermission('private_notes.read')
      && !this.privateNotes) {
      // La lead ha delle note interne non scaricate e
      // l'utente ha il permesso di vederle.

      // Scarica le note interne (tra un secondo)
      this.getPrivateNotesTimeout = setTimeout(async () => {
        console.log('OperationChildStatus: onTooltipShow() timeout!'); // eslint-disable-line no-console

        this.getPrivateNotes();
      }, 1000);
    }
  }

  onTooltipHide() {
    if (this.getSharedNotesTimeout !== undefined) clearTimeout(this.getSharedNotesTimeout);
    if (this.getPrivateNotesTimeout !== undefined) clearTimeout(this.getPrivateNotesTimeout);
  }

  onGoto(where: string) {
    console.log('OperationChildStatus: onGoto()'); // eslint-disable-line no-console

    switch (where) {
      case 'sharedNotes':
      case 'privateNotes':
      case 'attachments':
        this.$router.push({
          name: 'Lead',
          params: { code: leadRouteCode(`${this.operationChild?.id}`) },
          query: { tab: where },
        });
        break;

      case 'lead':
        this.$router.push({
          name: 'Lead',
          params: { code: leadRouteCode(`${this.operationChild?.id}`) },
          query: { tab: 'dashboard' },
        });
        break;

      default:
        break;
    }
  }
}
